/**
 * Runtime configuration.
 *
 * The deployed app fetches `/config.json`, which CDK writes into the site
 * bucket at deploy time. That keeps one built bundle deployable to any stage -
 * the API URL and Cognito ids do not need to exist when `vite build` runs.
 * Local development falls back to Vite env vars.
 */
export interface AppConfig {
  apiBaseUrl: string;
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  companyName: string;
}

let cached: AppConfig | undefined;

export async function loadConfig(): Promise<AppConfig> {
  if (cached) return cached;

  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (response.ok) {
      cached = (await response.json()) as AppConfig;
      return cached;
    }
  } catch {
    // Falls through to the dev defaults below.
  }

  cached = {
    apiBaseUrl: import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost:3000',
    region: import.meta.env['VITE_AWS_REGION'] ?? 'us-east-1',
    userPoolId: import.meta.env['VITE_USER_POOL_ID'] ?? '',
    userPoolClientId: import.meta.env['VITE_USER_POOL_CLIENT_ID'] ?? '',
    companyName: import.meta.env['VITE_COMPANY_NAME'] ?? 'our team',
  };
  return cached;
}

export function getConfig(): AppConfig {
  if (!cached) throw new Error('Configuration has not loaded yet');
  return cached;
}
